import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from '@/components/ui/select';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Target, ChevronDown, Upload, Image as ImageIcon, Sparkles, Loader2 } from 'lucide-react';
import { api, uploadFile } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import SEOScoreCard from '@/components/marketplace/SEOScoreCard';
import { resolveEffectiveOgImage, absoluteUrl } from '@/lib/ogImage';

// Static schema types accepted by the backend (must match SCHEMA_TYPES in models.py)
export const SCHEMA_TYPES = [
  { value: 'WebPage', label: 'WebPage (default)' },
  { value: 'AboutPage', label: 'AboutPage' },
  { value: 'ContactPage', label: 'ContactPage' },
  { value: 'FAQPage', label: 'FAQPage' },
  { value: 'Article', label: 'Article' },
  { value: 'CollectionPage', label: 'CollectionPage (category / index)' },
  { value: 'ItemPage', label: 'ItemPage (single listing)' },
  { value: 'Product', label: 'Product' },
  { value: 'Service', label: 'Service' },
  { value: 'CustomJsonLd', label: 'Custom JSON-LD' },
];

export const ROBOTS_META = [
  { value: 'index,follow', label: 'index, follow (default)' },
  { value: 'noindex,follow', label: 'noindex, follow' },
  { value: 'index,nofollow', label: 'index, nofollow' },
  { value: 'noindex,nofollow', label: 'noindex, nofollow' },
];

// Keyword-coverage checker used to render coloured indicators on the SEO card.
const keywordCoverage = (values) => {
  const kw = (values.focus_keyword || '').trim().toLowerCase();
  if (!kw) return null;
  const title = (values.meta_title || values.title || '').toLowerCase();
  const meta = (values.meta_description || '').toLowerCase();
  const excerpt = (values.excerpt || values.description || '').toLowerCase();
  const body = (values.html_content || values.content || values.description || values.excerpt || '').toLowerCase();
  const slug = (values.slug || '').toLowerCase();
  return {
    inTitle: title.includes(kw),
    inMeta: meta.includes(kw) || excerpt.includes(kw),
    inBody: body.includes(kw),
    inSlug: slug.includes(kw.replace(/\s+/g, '-')),
  };
};

const ChipStatus = ({ ok, label }) => (
  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${ok ? 'bg-emerald-500/10 border-emerald-500/40 text-emerald-700' : 'bg-muted text-muted-foreground border-border'}`}>
    {ok ? '✓' : '•'} {label}
  </span>
);

/**
 * SEOFieldsBlock — shared, reusable SEO editor block used across:
 *  - Static Pages CMS  (Review.pdf #11 — original ask)
 *  - Categories editor  (user extension)
 *  - Listings editor    (user extension)
 *
 * Props:
 *   values:  { focus_keyword, meta_title, meta_description, canonical_url,
 *              og_image_url, og_title, og_description, schema_type,
 *              robots_meta, schema_jsonld, [+context: title, slug, excerpt,
 *              description, html_content — used for keyword-coverage checks] }
 *   onChange(patch) — merges patch object into values (parent owns state)
 *   compact — smaller heading + collapsible, for use inside crowded forms
 *   allowAdvanced — show canonical/schema/robots/JSON-LD (default true).
 *                    Set false for seller-facing listings form.
 *   testIdPrefix — prefixes all data-testid attributes; required for stable
 *                    e2e testing when multiple SEOFieldsBlocks are on a page.
 */
export const SEOFieldsBlock = ({
  values = {},
  onChange,
  compact = false,
  allowAdvanced = true,
  testIdPrefix = 'seo',
  className = '',
  contentType = 'page',
}) => {
  const [open, setOpen] = useState(!compact);
  const [uploadingOg, setUploadingOg] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const { user } = useAuth();
  const isStaff = !!(user && (user.role === 'admin' || user.role === 'moderator'));
  const kw = keywordCoverage(values);
  const set = (k, v) => onChange && onChange({ [k]: v });
  // Effective OG preview: what the public share tags will actually use when no
  // manual image is set. Display-only — never written back into the form, so an
  // automatic image can't silently become a persisted manual selection.
  const autoOgImage = values.og_image_url ? null : resolveEffectiveOgImage(values, contentType);

  /**
   * Regenerate SEO copy from the current form context using the backend
   * LLM helper. Only fills fields that are currently blank — never
   * overwrites what the staff editor already typed, to avoid destructive
   * clicks. If everything is already filled, a single click gives you
   * fresh suggestions in all fields (feels like "give me another take").
   */
  const regenerate = async () => {
    if (!isStaff) return;
    setRegenerating(true);
    try {
      const { data } = await api.post('/admin/seo/generate', {
        content_type: contentType,
        entity: values,
      });
      const patch = {};
      // Prefer to only fill blanks — but if the user clicks the button
      // while everything is filled, replace every field so the click
      // has a visible effect. Rule: fill blanks first, else overwrite.
      const anyBlank = !values.meta_title || !values.meta_description || !values.focus_keyword;
      const fields = [
        'meta_title', 'meta_description', 'focus_keyword',
        'og_title', 'og_description', 'og_image_alt',
      ];
      for (const f of fields) {
        if (!data[f]) continue;
        if (anyBlank ? !values[f] : true) patch[f] = data[f];
      }
      if (Object.keys(patch).length === 0) {
        toast.info('Nothing to regenerate — all fields already look good.');
      } else {
        onChange && onChange(patch);
        toast.success('SEO fields regenerated with AI');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI regeneration failed');
    } finally { setRegenerating(false); }
  };

  const uploadOg = async (file) => {
    if (!file) return;
    setUploadingOg(true);
    try {
      const r = await uploadFile(file, 'image', { context: 'og_image' });
      const url = r.public_url?.startsWith('http') ? r.public_url : `${process.env.REACT_APP_BACKEND_URL || ''}${r.public_url}`;
      set('og_image_url', url);
      toast.success('OG image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploadingOg(false); }
  };

  const Body = (
    <div className='space-y-4'>
      {isStaff && (
        <div className='flex items-center justify-end'>
          <Button
            type='button'
            variant='outline'
            size='sm'
            onClick={regenerate}
            disabled={regenerating}
            data-testid={`${testIdPrefix}-regenerate-ai`}
            className='gap-1.5'
            title='Regenerate meta title, description, focus keyword, and OG copy using Gemini via Emergent LLM. Fills blanks first, then overwrites on repeat clicks.'
          >
            {regenerating ? <Loader2 className='h-3.5 w-3.5 animate-spin' /> : <Sparkles className='h-3.5 w-3.5' />}
            {regenerating ? 'Generating…' : 'Regenerate with AI'}
          </Button>
        </div>
      )}
      {isStaff && (
        <SEOScoreCard
          values={values}
          contentType={contentType}
          className='mb-2'
        />
      )}
      <div className='grid md:grid-cols-2 gap-4'>
        <div>
          <Label>Focus keyword</Label>
          <Input
            value={values.focus_keyword || ''}
            maxLength={120}
            placeholder='e.g. used cnc lathe'
            onChange={(e) => set('focus_keyword', e.target.value)}
            data-testid={`${testIdPrefix}-focus-keyword`}
          />
          <div className='text-[11px] text-muted-foreground mt-1'>Primary phrase this page should rank for.</div>
        </div>
        <div>
          <Label>Meta title</Label>
          <Input
            value={values.meta_title || ''}
            maxLength={70}
            placeholder='Auto-generated from title if left empty'
            onChange={(e) => set('meta_title', e.target.value)}
            data-testid={`${testIdPrefix}-meta-title`}
          />
          <div className='text-[11px] text-muted-foreground text-right mt-0.5'>{(values.meta_title || '').length}/70</div>
        </div>
        {isStaff && contentType === 'listing' && (
          <div className='md:col-span-2'>
            <Label>URL slug</Label>
            <Input
              value={values.slug || ''}
              maxLength={80}
              placeholder='auto-generated-from-title'
              onChange={(e) => set('slug', e.target.value)}
              data-testid={`${testIdPrefix}-slug`}
            />
            <div className='text-[11px] text-muted-foreground mt-1'>
              Last segment of the listing URL — edit it to include your focus keyword. Changing it keeps the old URL working (it auto-redirects to the new one). A short id suffix is preserved automatically to keep the link unique.
            </div>
            {kw && (
              <div className='mt-1'><ChipStatus ok={kw.inSlug} label='Keyword in slug' /></div>
            )}
          </div>
        )}
        <div className='md:col-span-2'>
          <Label>Meta description</Label>
          <Textarea
            rows={2}
            maxLength={170}
            value={values.meta_description || ''}
            onChange={(e) => set('meta_description', e.target.value)}
            data-testid={`${testIdPrefix}-meta-description`}
          />
          <div className='text-[11px] text-muted-foreground text-right mt-0.5'>{(values.meta_description || '').length}/170</div>
        </div>
      </div>

      <div className='pt-4 border-t space-y-4'>
        <div className='text-[11px] uppercase tracking-wider text-muted-foreground font-semibold'>Social sharing (Open Graph)</div>
        <div>
          <Label>OG image</Label>
          <div className='flex items-center gap-3'>
            {values.og_image_url ? (
              <img src={values.og_image_url} alt='og preview' className='h-16 w-28 object-cover rounded border' data-testid={`${testIdPrefix}-og-image-manual-preview`} />
            ) : autoOgImage ? (
              <img src={absoluteUrl(autoOgImage)} alt='auto og preview' className='h-16 w-28 object-cover rounded border opacity-80' data-testid={`${testIdPrefix}-og-image-auto-preview`} />
            ) : (
              <div className='h-16 w-28 rounded border bg-muted flex items-center justify-center text-muted-foreground'>
                <ImageIcon className='h-5 w-5' />
              </div>
            )}
            <label className='inline-flex items-center gap-1.5 rounded-md border px-3 py-2 cursor-pointer text-sm hover:bg-secondary'>
              <Upload className='h-4 w-4' /> {uploadingOg ? 'Uploading…' : (values.og_image_url ? 'Replace' : 'Upload')}
              <input
                type='file'
                accept='image/*'
                className='hidden'
                onChange={(e) => { uploadOg(e.target.files?.[0]); e.target.value = ''; }}
                data-testid={`${testIdPrefix}-og-image-upload`}
              />
            </label>
            <Input
              value={values.og_image_url || ''}
              placeholder='…or paste URL (falls back to cover image)'
              onChange={(e) => set('og_image_url', e.target.value)}
              className='flex-1'
              data-testid={`${testIdPrefix}-og-image-url`}
            />
          </div>
          {!values.og_image_url && (
            <div className='text-[11px] text-muted-foreground mt-1' data-testid={`${testIdPrefix}-og-image-effective-note`}>
              {autoOgImage
                ? 'No manual OG image — social previews will automatically use the primary content image above. Upload or paste a URL to override it.'
                : 'No OG image available yet. Social previews will fall back to the site default.'}
            </div>
          )}
        </div>
        <div>
          <Label>OG image alt text</Label>
          <Input
            value={values.og_image_alt || ''}
            maxLength={200}
            placeholder='Describe the image for screen readers & SEO — auto-filled on save if left blank'
            onChange={(e) => set('og_image_alt', e.target.value)}
            data-testid={`${testIdPrefix}-og-image-alt`}
          />
          <div className='text-[11px] text-muted-foreground mt-1'>
            Used as the <code>alt</code> attribute on the OG image. Auto-populates from the focus keyword or title — edit anytime.
          </div>
        </div>
        <div className='grid md:grid-cols-2 gap-4'>
          <div>
            <Label>OG title (defaults to meta title)</Label>
            <Input
              value={values.og_title || ''}
              maxLength={200}
              onChange={(e) => set('og_title', e.target.value)}
              data-testid={`${testIdPrefix}-og-title`}
            />
          </div>
          <div>
            <Label>OG description</Label>
            <Input
              value={values.og_description || ''}
              maxLength={320}
              onChange={(e) => set('og_description', e.target.value)}
              data-testid={`${testIdPrefix}-og-description`}
            />
          </div>
        </div>
      </div>

      {allowAdvanced && (
        <div className='pt-4 border-t space-y-4'>
          <div className='text-[11px] uppercase tracking-wider text-muted-foreground font-semibold'>Advanced</div>
          <div className='grid md:grid-cols-2 gap-4'>
            <div>
              <Label>Canonical URL</Label>
              <Input
                value={values.canonical_url || ''}
                placeholder='e.g. https://indusfact.com/sell/used-cnc-lathe'
                onChange={(e) => set('canonical_url', e.target.value)}
                data-testid={`${testIdPrefix}-canonical`}
              />
              <div className='text-[11px] text-muted-foreground mt-1'>Leave blank to use the page&apos;s own URL. Set to point search engines at the original when this is a duplicate.</div>
            </div>
            <div>
              <Label>Schema.org type</Label>
              <Select value={values.schema_type || undefined} onValueChange={(v) => set('schema_type', v)}>
                <SelectTrigger data-testid={`${testIdPrefix}-schema-type`}>
                  <SelectValue placeholder='WebPage (default)' />
                </SelectTrigger>
                <SelectContent>
                  {SCHEMA_TYPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Robots meta</Label>
              <Select value={values.robots_meta || undefined} onValueChange={(v) => set('robots_meta', v)}>
                <SelectTrigger data-testid={`${testIdPrefix}-robots`}>
                  <SelectValue placeholder='index, follow (default)' />
                </SelectTrigger>
                <SelectContent>
                  {ROBOTS_META.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {values.schema_type === 'CustomJsonLd' && (
              <div className='md:col-span-2'>
                <Label>Custom JSON-LD (raw)</Label>
                <Textarea
                  rows={4}
                  maxLength={4000}
                  value={values.schema_jsonld || ''}
                  onChange={(e) => set('schema_jsonld', e.target.value)}
                  className='font-mono text-xs'
                  placeholder='{ "@context": "https://schema.org", "@type": "…" }'
                  data-testid={`${testIdPrefix}-jsonld`}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* SEO Preview panel — mimics Google search result */}
      <div className='pt-4 border-t space-y-2'>
        <div className='text-[11px] uppercase tracking-wider text-muted-foreground font-semibold'>Google preview</div>
        <div className='rounded-md border bg-white p-3 max-w-[600px]'>
          <div className='text-[13px] text-muted-foreground truncate'>
            {values.canonical_url || 'https://indusfact.com/…'}
          </div>
          <div className='text-[18px] font-medium text-[#1a0dab] leading-tight truncate mt-0.5'>
            {values.meta_title || values.title || 'Your page title will appear here'}
          </div>
          <div className='text-[13px] text-neutral-700 line-clamp-2 mt-1'>
            {values.meta_description || 'Your meta description will appear here. Aim for 120-160 chars.'}
          </div>
        </div>
      </div>
    </div>
  );

  if (!compact) {
    return (
      <div className={`space-y-3 ${className}`} data-testid={`${testIdPrefix}-block`}>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2'>
            <Target className='h-4 w-4 text-primary' />
            <div className='font-semibold'>SEO &amp; Social</div>
          </div>
          {kw && (
            <div className='flex flex-wrap gap-1' data-testid={`${testIdPrefix}-keyword-status`}>
              <ChipStatus ok={kw.inTitle} label='Title' />
              <ChipStatus ok={kw.inMeta} label='Meta' />
              <ChipStatus ok={kw.inBody} label='Body' />
              <ChipStatus ok={kw.inSlug} label='Slug' />
            </div>
          )}
        </div>
        {Body}
      </div>
    );
  }

  // Compact/collapsible variant — used inside listings form
  return (
    <Collapsible open={open} onOpenChange={setOpen} className={className}>
      <CollapsibleTrigger asChild>
        <Button variant='outline' type='button' size='sm' data-testid={`${testIdPrefix}-toggle`}>
          <Target className='h-4 w-4 mr-1.5' />
          {open ? 'Hide SEO options' : 'Show SEO options'}
          <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${open ? 'rotate-180' : ''}`} />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className='mt-4 pt-4 border-t'>
        {Body}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default SEOFieldsBlock;
